import { useUpdateSearchCandidateProfileMessageMutation } from 'generated-graphql/hooks/apollo-hooks';

type RegenerateFromIndexFn = (agentMessageId: string) => {
  success: boolean;
  userMessageBody: string | null;
  removedMessageIds: string[];
};

type UseConversationRegenerationProps = {
  regenerateFromIndex: RegenerateFromIndexFn;
};

/**
 * Manages conversation regeneration by coordinating message removal
 * and marking removed messages as overwritten in the backend.
 */
export const useConversationRegeneration = ({
  regenerateFromIndex
}: UseConversationRegenerationProps) => {
  const [updateMessage] = useUpdateSearchCandidateProfileMessageMutation();

  const markMessagesAsOverwritten = async (messageIds: string[]) => {
    if (messageIds.length === 0) return;

    // Update all messages in parallel
    const updatePromises = messageIds.map((messageId) =>
      updateMessage({
        variables: {
          input: {
            id: messageId,
            isOverwritten: true
          }
        }
      })
    );

    await Promise.allSettled(updatePromises);
  };

  const handleRegenerateFromMessage = (
    agentMessageId: string
  ): string | null => {
    const result = regenerateFromIndex(agentMessageId);

    if (!result.success) {
      return null;
    }

    // Mark removed messages as overwritten in the backend
    markMessagesAsOverwritten(result.removedMessageIds);

    return result.userMessageBody;
  };

  return {
    handleRegenerateFromMessage,
    markMessagesAsOverwritten
  };
};
